/**
 * Why the connector can't log in to the VMS, in words support can act on
 * (ENG-2252, after SUP-793).
 *
 * The connector records its last failed VMS login per camera as
 * `last_connection_error` (on `camera/{id}/status/` and every
 * `camera_liveness/` row; the newest site-scope one is also the liveness
 * response's top-level `connection_error`). It is null when there is no
 * error, or once a later login succeeds.
 *
 * SUP-793: an Exacq site sent no alerts for 12 days. Its login answered
 * HTTP 502 with an empty errorText because the Exacq web service could not
 * reach its recording server; restarting the Exacq server fixed it. Support
 * changed the Server ID instead, which then answered HTTP 400 "invalid server
 * id; 1". Neither response was visible anywhere. An unauthenticated GET / on
 * Exacq returns 200 while login fails, so "the page loads" is not health.
 */
import { ConnectionError, SettingsCompare } from "../troubleshootingInterfaces";
import { formatDateTime, formatRelative } from "./formatters";

export type ConnectionErrorOwner = "customer" | "actuate" | "unknown";

export type ConnectionErrorCategory =
  | "exacq-recording-server"
  | "server-id"
  | "network"
  | "credentials"
  | "no-session"
  | "other";

export interface ConnectionErrorAdvice {
  category: ConnectionErrorCategory;
  title: string;
  action: string;
  owner: ConnectionErrorOwner;
  severity: "bad";
  raw: {
    code: string;
    /** The VMS's own words, exactly as recorded ("" stays ""). */
    detail: string;
    /** `detail` for display: "(empty response)" when the VMS said nothing. */
    detailText: string;
    at: string | null;
    /** `at` relative to now, "unknown" when absent. */
    atRelative: string;
    /** Start of the unbroken failure streak; `at` when admin didn't send it. */
    firstFailedAt: string | null;
    firstFailedRelative: string;
  };
  /** "Failing since Oct 6, 9:14 AM (12 d ago)", or "" with no time at all. */
  failingSince: string;
  /** Staff detail line: `http_502 · (empty response) · failing since 12 d ago · latest 3 min ago`. */
  detailLine: string;
}

export interface ConnectionErrorContext {
  /**
   * The Server ID the site had when it last worked, from the ENG-2244
   * "Compare to when it last worked" diff, when that has been run.
   */
  lastWorkingServerId?: string | null;
  nowMs?: number;
}

const NETWORK_CODES = ["timeout", "connection_refused", "dns", "tls"];

const NETWORK_ACTION =
  "We can't reach the VMS at its address. Check the site's internet connection, firewall/port forwarding, or VPN.";

const NETWORK_TITLES: Record<string, string> = {
  timeout: "The VMS isn't answering (timed out)",
  connection_refused: "The VMS refused the connection",
  dns: "The VMS address doesn't resolve",
  tls: "The secure connection to the VMS fails",
};

/** Accepts whatever admin sent; anything without a code is "no error". */
export const normalizeConnectionError = (
  raw: unknown
): ConnectionError | null => {
  if (!raw || typeof raw !== "object") return null;
  const value = raw as Record<string, unknown>;
  const code = typeof value.code === "string" ? value.code.trim() : "";
  if (!code) return null;
  const at = typeof value.at === "string" && value.at ? value.at : null;
  return {
    code,
    detail: typeof value.detail === "string" ? value.detail : "",
    at,
    // Never null when present (admin falls back to `at` on older rows).
    first_failed_at:
      typeof value.first_failed_at === "string" && value.first_failed_at
        ? value.first_failed_at
        : at,
    integration:
      typeof value.integration === "string" && value.integration
        ? value.integration
        : null,
    scope: value.scope === "site" ? "site" : "camera",
  };
};

export const connectionErrorDetailText = (detail: string): string =>
  detail.trim() ? detail.trim() : "(empty response)";

/**
 * Where the Server ID lives in a compare: settings.json `customer.server`
 * (admin `Customer.server_id`, Exacq's login param `s`), or a model-field
 * style path ending in `server_id`.
 */
const isServerIdPath = (path: string): boolean => {
  if (path === "customer.server") return true;
  const key =
    path
      .split(/[.[\]]/)
      .filter(Boolean)
      .pop() ?? path;
  return key === "server_id";
};

const displayValue = (value: unknown): string =>
  typeof value === "string" ? value : JSON.stringify(value);

/**
 * The Server ID from when the site last worked, read off an already-run
 * compare: a server-id setting whose baseline value differs from now.
 */
export const lastWorkingServerId = (
  compare: SettingsCompare | null | undefined
): string | null => {
  if (!compare) return null;
  for (const source of compare.sources ?? []) {
    for (const change of source.changes ?? []) {
      if (
        isServerIdPath(change.path) &&
        change.from !== undefined &&
        change.from !== null &&
        change.from !== "" &&
        JSON.stringify(change.from) !== JSON.stringify(change.to)
      )
        return displayValue(change.from);
    }
  }
  return null;
};

export const connectionErrorAdvice = (
  error: ConnectionError,
  ctx: ConnectionErrorContext = {}
): ConnectionErrorAdvice => {
  const nowMs = ctx.nowMs ?? Date.now();
  const code = (error.code || "").trim();
  const lower = code.toLowerCase();
  const detail = error.detail ?? "";
  const detailText = connectionErrorDetailText(detail);
  const atRelative = error.at ? formatRelative(error.at, nowMs) : "unknown";
  const firstFailedAt = error.first_failed_at || error.at || null;
  const firstFailedRelative = firstFailedAt
    ? formatRelative(firstFailedAt, nowMs)
    : "unknown";
  const raw = {
    code,
    detail,
    detailText,
    at: error.at,
    atRelative,
    firstFailedAt,
    firstFailedRelative,
  };
  const failingSince = firstFailedAt
    ? `Failing since ${formatDateTime(firstFailedAt)} (${firstFailedRelative})`
    : "";
  const detailLine = [
    code,
    detailText,
    firstFailedAt ? `failing since ${firstFailedRelative}` : "time unknown",
    error.at ? `latest ${atRelative}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
  const make = (
    category: ConnectionErrorCategory,
    title: string,
    action: string,
    owner: ConnectionErrorOwner
  ): ConnectionErrorAdvice => ({
    category,
    title,
    action,
    owner,
    severity: "bad",
    raw,
    failingSince,
    detailLine,
  });

  if (
    (error.integration ?? "").toLowerCase() === "exacq" &&
    lower === "http_502" &&
    !detail.trim()
  )
    return make(
      "exacq-recording-server",
      "Exacq can't reach its recording server",
      "Exacq's web service is running but can't reach the recording server behind it. Ask the site to restart the Exacq server (or its recording service). Our connector reconnects on its own within a minute; no change on our side is needed.",
      "customer"
    );

  if (lower === "http_400" && /invalid server id/i.test(detail)) {
    const previous = ctx.lastWorkingServerId;
    return make(
      "server-id",
      "Server ID setting is wrong",
      `The VMS rejected the Server ID. Check the site's Server ID setting. ${
        previous
          ? `It was ${previous} when this site last worked.`
          : "Use Compare to when it last worked to see the previous value."
      }`,
      "actuate"
    );
  }

  if (NETWORK_CODES.includes(lower))
    return make(
      "network",
      NETWORK_TITLES[lower],
      lower === "tls"
        ? "We reach the VMS, but its secure connection fails: its certificate or HTTPS setup is rejected. Check the VMS's certificate (expired or replaced) and that the site's port and HTTP/HTTPS setting match the VMS."
        : NETWORK_ACTION,
      lower === "tls" ? "unknown" : "customer"
    );

  if (lower === "http_401" || lower === "http_403")
    return make(
      "credentials",
      "The VMS rejected our login",
      "The VMS rejected our login. Check the username and password on this site.",
      "unknown"
    );

  if (lower === "no_session")
    return make(
      "no-session",
      "The VMS gave us no session",
      "The VMS answered but didn't give us a session. Check the account's permissions and the VMS license.",
      "customer"
    );

  return make(
    "other",
    "The VMS login is failing",
    `The VMS login fails with ${code}: ${detailText}. Copy the diagnostic report into a ticket so Actuate can look at it.`,
    "unknown"
  );
};
